import { Search, Activity, Boxes, Workflow } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

const features = [
  {
    key: 'search',
    icon: Search,
    color: 'text-info-500',
    bg: 'bg-info-50 dark:bg-info-500/10',
    border: 'border-info-200 dark:border-info-500/20',
    metric: '<200ms',
    metricLabel: 'P95 search latency',
  },
  {
    key: 'anomaly',
    icon: Activity,
    color: 'text-warning-500',
    bg: 'bg-warning-50 dark:bg-warning-500/10',
    border: 'border-warning-200 dark:border-warning-500/20',
    metric: 'Up to 85%',
    metricLabel: 'fewer false alerts',
  },
  {
    key: 'templates',
    icon: Boxes,
    color: 'text-success-500',
    bg: 'bg-success-50 dark:bg-success-500/10',
    border: 'border-success-200 dark:border-success-500/20',
    metric: '22+',
    metricLabel: 'Managed pipeline templates',
  },
  {
    key: 'pipelines',
    icon: Workflow,
    color: 'text-primary-500',
    bg: 'bg-primary-50 dark:bg-primary-500/10',
    border: 'border-primary-200 dark:border-primary-500/20',
    metric: '16',
    metricLabel: 'node types',
  },
] as const;

export function FeaturesSection() {
  const t = useT();

  return (
    <section id="features" className="py-24 sm:py-32 gradient-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mx-auto text-center mb-16 sm:mb-20 animate-on-scroll">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
            {t('features.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Feature cards — Feature → Benefit → Proof */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.key}
                className="animate-on-scroll glass-subtle card-hover rounded-2xl p-8 flex flex-col"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Icon */}
                <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center border', feature.bg, feature.border)}>
                  <Icon className={cn('h-6 w-6', feature.color)} />
                </div>

                {/* Feature title */}
                <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">
                  {t(`features.${feature.key}.title`)}
                </h3>

                {/* Benefit-driven description */}
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                  {t(`features.${feature.key}.description`)}
                </p>

                {/* Proof metric */}
                <div className="mt-5 pt-4 border-t border-gray-200/60 dark:border-gray-700/30 flex items-baseline gap-2">
                  <span className="text-2xl font-extrabold text-gradient">{feature.metric}</span>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{feature.metricLabel}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
